/**
 * CivicPulse — full-screen Civic Map with filter sidebar. Powers
 * /civic/map, /civic/india/*, /civic/india/kerala/*, etc.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import { Filter, MapPin, ChevronLeft, Map as MapIcon, List as ListIcon, AlertTriangle } from "lucide-react";
import CivicMap, { MapLegend } from "@/components/civic/CivicMap";
import { civic, photoUrl } from "./api";
import { civicPath } from "./basePath";

const URGENCY_OPTIONS = [
  { key: "",          label: "All severities",        color: "#6B7280" },
  { key: "low",       label: "Low",                    color: "#16A34A" },
  { key: "medium",    label: "Medium",                 color: "#EAB308" },
  { key: "high",      label: "High",                   color: "#F97316" },
  { key: "emergency", label: "Emergency (call 112!)",  color: "#DC2626" },
];

export default function CivicMapPage({ geoPathOverride } = {}) {
  const search = new URLSearchParams(useLocation().search);
  const params = useParams();
  const navigate = useNavigate();

  // Build geo_path from the URL params (country/state/district/city).
  const geoPath = useMemo(() => {
    if (geoPathOverride) return geoPathOverride;
    const parts = [
      params.country, params.state, params.district, params.city,
    ].filter(Boolean).map((s) => s.toLowerCase());
    return parts.length ? "/" + parts.join("/") : "";
  }, [geoPathOverride, params]);

  const [category, setCategory] = useState(search.get("category") || "");
  const [status, setStatus] = useState(search.get("status") || "");
  const [urgency, setUrgency] = useState(search.get("urgency") || "");
  const [view, setView] = useState(search.get("view") === "list" ? "list" : "map");
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [issues, setIssues] = useState([]);
  const [counters, setCounters] = useState({});
  const [totals, setTotals] = useState({ total: 0, resolved: 0, open: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    civic.categories().then((d) => {
      setCategories(d.items || []);
      setStatuses(d.statuses || []);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 400 };
    if (category) params.category = category;
    if (status) params.status = status;
    if (urgency) params.urgency = urgency;
    if (geoPath) params.geo_path = geoPath;
    Promise.all([
      civic.listIssues(params),
      civic.counters(geoPath || undefined),
    ]).then(([listed, counts]) => {
      setIssues(listed.items || []);
      setCounters(counts.by_category || {});
      setTotals({ total: counts.total, resolved: counts.resolved, open: counts.open });
    }).finally(() => setLoading(false));
  }, [category, status, urgency, geoPath]);

  const categoryColors = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.slug, c.color])),
    [categories]
  );

  const title = geoPath
    ? geoPath.split("/").filter(Boolean).map((s) => s[0].toUpperCase() + s.slice(1)).join(" / ")
    : "Civic Map";

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="civic-map-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
          <Link to={civicPath("/")} className="hover:text-emerald-700 inline-flex items-center gap-1">
            <ChevronLeft className="w-3.5 h-3.5" /> Home
          </Link>
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900">
              <MapPin className="inline w-6 h-6 -mt-1 text-emerald-700 mr-2" />
              {title}
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              {totals.total.toLocaleString()} total • {totals.open.toLocaleString()} open • {totals.resolved.toLocaleString()} resolved
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Map ↔ List view toggle */}
            <div className="inline-flex rounded-full bg-white ring-1 ring-emerald-200 p-1 text-sm font-semibold"
                 data-testid="civic-map-view-toggle">
              <button onClick={() => setView("map")}
                      data-testid="civic-view-map"
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition ${
                        view === "map" ? "bg-emerald-700 text-white shadow" : "text-slate-600 hover:text-emerald-700"
                      }`}>
                <MapIcon className="w-4 h-4" /> Map
              </button>
              <button onClick={() => setView("list")}
                      data-testid="civic-view-list"
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition ${
                        view === "list" ? "bg-emerald-700 text-white shadow" : "text-slate-600 hover:text-emerald-700"
                      }`}>
                <ListIcon className="w-4 h-4" /> List
              </button>
            </div>
            <Link to={civicPath("/report")}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-5 py-2.5 transition"
                  data-testid="civic-map-report-cta">
              Report a Problem
            </Link>
          </div>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-5">
          {/* Sidebar filters */}
          <aside className="space-y-4">
            <div className="rounded-2xl bg-white ring-1 ring-emerald-100 p-4">
              <div className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <Filter className="w-4 h-4 text-emerald-700" /> Filters
              </div>
              <div className="text-xs font-semibold text-slate-500 mt-3 mb-1">Category</div>
              <select data-testid="civic-map-filter-category"
                      value={category} onChange={(e) => setCategory(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label_en}</option>
                ))}
              </select>
              <div className="text-xs font-semibold text-slate-500 mt-3 mb-1">Status</div>
              <select data-testid="civic-map-filter-status"
                      value={status} onChange={(e) => setStatus(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">All statuses</option>
                {statuses.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              <div className="text-xs font-semibold text-slate-500 mt-3 mb-1">Severity</div>
              <select data-testid="civic-map-filter-urgency"
                      value={urgency} onChange={(e) => setUrgency(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                {URGENCY_OPTIONS.map((u) => (
                  <option key={u.key || "all"} value={u.key}>{u.label}</option>
                ))}
              </select>
              {urgency === "emergency" && (
                <p className="mt-2 text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded-lg p-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  For life-threatening emergencies, dial <b>112</b> — CivicPulse is not an emergency service.
                </p>
              )}
            </div>
            <MapLegend categories={categories.slice(0, 8)} counters={counters} />
          </aside>

          <div>
            {view === "map" && (
              <CivicMap issues={issues} categoryColors={categoryColors} height={560} />
            )}
            {view === "list" && (
              <div className="rounded-2xl bg-white ring-1 ring-emerald-100 p-4">
                <div className="text-sm font-semibold text-slate-700 mb-3">
                  {issues.length.toLocaleString()} issue{issues.length === 1 ? "" : "s"} in this view
                </div>
                {loading ? (
                  <div className="text-sm text-slate-500 italic py-8 text-center">Loading issues…</div>
                ) : issues.length === 0 ? (
                  <div className="text-sm text-slate-500 italic py-8 text-center">
                    No issues match these filters.
                  </div>
                ) : (
                  <ul className="divide-y divide-emerald-50" data-testid="civic-issues-list">
                    {issues.map((it) => (
                      <IssueRow key={it.public_id} issue={it} categoryColors={categoryColors} />
                    ))}
                  </ul>
                )}
              </div>
            )}
            {view === "map" && (
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {loading ? (
                  <div className="col-span-full text-sm text-slate-500 italic">Loading issues…</div>
                ) : issues.length === 0 ? (
                  <div className="col-span-full text-sm text-slate-500 italic">
                    No issues in this view yet. <Link to={civicPath("/report")} className="underline hover:no-underline text-emerald-700">Be the first to report one.</Link>
                  </div>
                ) : (
                  issues.slice(0, 24).map((it) => (
                    <IssueCard key={it.public_id} issue={it} categoryColors={categoryColors} />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function IssueCard({ issue, categoryColors }) {
  const color = categoryColors[issue.category] || "#0F7B3F";
  return (
    <Link
      to={civicPath(`/issue/${issue.public_id}`)}
      className="rounded-2xl bg-white ring-1 ring-emerald-100 hover:ring-emerald-300 hover:shadow transition overflow-hidden"
      data-testid={`civic-issue-card-${issue.public_id}`}
    >
      {issue.photo_urls?.[0] && (
        <img src={photoUrl(issue.photo_urls[0])} alt=""
             className="w-full h-32 object-cover" loading="lazy" />
      )}
      <div className="p-3">
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color }}>
          {issue.category}
        </div>
        <div className="font-semibold text-slate-800 mt-1 line-clamp-2">{issue.title}</div>
        <div className="text-xs text-slate-500 mt-1 truncate">
          {issue.location?.address || `${issue.location?.city || issue.location?.district || ""}`}
        </div>
        <div className="text-xs text-emerald-800 mt-1">{issue.status?.replace(/_/g, " ")}</div>
      </div>
    </Link>
  );
}
